/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { store } from "store";
import qs from "qs";

import { Alert } from "UpduoLibrary";
import { logoutResetState } from "config/ResetState";
import * as UpduoAnalytics from "config/UpduoAnalytics";

const apiDict: Record<
  string,
  {
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    urlTail: string;
    primary?: boolean;
    public?: boolean;
  }
> = {
  // REST-AUTH
  postSignUp: { method: "POST", urlTail: "/api/registration/", public: true },
  postLogin: { method: "POST", urlTail: "/api/durin/login/", public: true },
  postLoginAuth0: {
    method: "POST",
    urlTail: "/api/rest-auth/auth0/",
    public: true,
  },
  postLoginFacebook: {
    method: "POST",
    urlTail: "/api/rest-auth/facebook/",
    public: true,
  },
  postLoginGoogle: {
    method: "POST",
    urlTail: "/api/rest-auth/google/",
    public: true,
  },
  postLoginSalesforce: {
    method: "POST",
    urlTail: "/api/rest-auth/salesforce/",
    public: true,
  },
  postLogoutMember: { method: "POST", urlTail: "/api/durin/logout/" },

  // SKALE: PRIMARY
  postLogout: {
    method: "POST",
    urlTail: "/api/durin/logout/",
    primary: true,
  },
  getPrimary: { method: "GET", urlTail: "/api/users/me/", primary: true },
  getPrimaryMemberList: {
    method: "GET",
    urlTail: "/api/users/primary/member/list/",
    primary: true,
  },
  patchMePrimary: {
    method: "PATCH",
    urlTail: "/api/users/me/",
    primary: true,
  },

  // SESSIONS SECTION
  getMatchStatus: { method: "GET", urlTail: "/api/session/match/" }, // needs query string
  postMatch: { method: "POST", urlTail: "/api/session/match/" },
  postMatchAck: { method: "POST", urlTail: "/api/session/match/ack/" },
  getCheckAppVersion: { method: "GET", urlTail: "/api/users/check/version/" },
  getCheckTimeWindow: {
    method: "GET",
    urlTail: "/api/users/check/organization/",
  },
  postSessionStatePoll: {
    method: "POST",
    urlTail: "/api/session/poll/",
  },

  // SKALE: STAFF
  getTimeSeriesData: {
    method: "GET",
    urlTail: "/api/staff/data/",
  }, // needs query string,
  getCurriculumStatSkillList: {
    method: "GET",
    urlTail: "/api/staff/curriculum_stat/skill/",
  },
  getCurriculumStatSkillModuleList: {
    method: "GET",
    urlTail: "/api/staff/curriculum_stat/skill_module/",
  },
  getSkillProgressUserList: {
    method: "GET",
    urlTail: "/api/staff/users/list/",
  }, // needs query string
  getSkillProgressStoreList: {
    method: "GET",
    urlTail: "/api/staff/users/store/",
  }, // needs query string
  getSessionProgressStoreList: {
    method: "GET",
    urlTail: "/api/staff/users/session_progress/store/",
  }, // needs query string
  getSessionProgressUserList: {
    method: "GET",
    urlTail: "/api/staff/users/session_progress/user/",
  }, // needs query string

  // SKALE: USER
  getSummitDetailWithCode: {
    method: "GET",
    urlTail: "/api/users/summit/code/",
  }, // needs value
  getPhotoSignedUrls: {
    method: "GET",
    urlTail: "/api/users/profile/img/signed_urls/",
  },
  postActiveDevice: { method: "POST", urlTail: "/api/users/device/active/" },
  getAccountSettings: {
    method: "GET",
    urlTail: "/api/users/settings/account/",
  },
  postAccountSettings: {
    method: "POST",
    urlTail: "/api/users/settings/account/",
  },

  // SKALE: PERFORMANCE
  getMetricMetadataList: {
    method: "GET",
    urlTail: "/api/performance/metricmetadata/list/",
  },
  postMetricMetadataNew: {
    method: "POST",
    urlTail: "/api/performance/metricmetadata/list/",
  },
  postUserMetricNew: {
    method: "POST",
    urlTail: "/api/performance/usermetric/list/create/user/",
  },
  getOrgUserList: {
    method: "GET",
    urlTail: "/api/performance/users/org_list/",
  },
  deleteMetricMetadataNew: {
    method: "DELETE",
    urlTail: "/api/performance/metricmetadata/list/",
  },
  getMetricList: { method: "GET", urlTail: "/api/performance/metric/list/" },
};

interface UpduoServiceInterface {
  simpleHeaders:
    | undefined
    | { "Content-Type": string; "Upduo-Version": string };
  disableApis: boolean;
  alertVisible: boolean;
}

const UpduoServiceObject: UpduoServiceInterface = {
  simpleHeaders: undefined,
  disableApis: false,
  alertVisible: false,
};

export function defineApiHeaders(callback = () => {}) {
  asyncDefineApiHeaders().then(callback);
}

export async function asyncDefineApiHeaders() {
  UpduoServiceObject.simpleHeaders = {
    "Content-Type": "application/json",
    "Upduo-Version": `Web/${process.env.REACT_APP_SKALE_WEB_VERSION}`,
  };
}

function authHeaders(authToken: string) {
  const extraHeaders = {
    Accept: "application/json",
    Authorization: `Token ${authToken}`,
  };
  const result = Object.assign(
    {},
    UpduoServiceObject.simpleHeaders,
    extraHeaders
  );
  return result;
}

export function showDisconnectedAlert() {
  // Only one disconnection alert should be visible at any time
  if (UpduoServiceObject.alertVisible) {
    return;
  }
  UpduoServiceObject.alertVisible = true;
  Alert(
    "Upduo can't connect to the internet.",
    "Please check your internet connection and try again.",
    [
      {
        text: "Okay",
        onPress: () => {
          UpduoServiceObject.alertVisible = false;
          UpduoAnalytics.logEvent("user-dismiss-disconnection-alert");
        },
      },
    ]
    // { cancelable: false }
  );
}

// TODO this should probably be turned into an object argument (named parameters) for clarity
export function callApi(
  apiKey: string,
  body: any,
  successCallback?: Function,
  failureCallback?: Function,
  extraValue?: number,
  queryString?: string
) {
  const reduxState = store.getState() as any;
  if (!UpduoServiceObject.simpleHeaders) {
    // If basic header is not defined yet, set it and try again
    defineApiHeaders(() => {
      callApi(
        apiKey,
        body,
        successCallback,
        failureCallback,
        extraValue,
        queryString
      );
    });
    return;
  }
  let apiUrl = process.env.REACT_APP_SERVER_URL! + apiDict[apiKey].urlTail;
  if (extraValue || extraValue === 0) {
    apiUrl += `${extraValue}/`;
  }
  if (queryString) {
    apiUrl += queryString;
  }
  // console.log("API request sent for " + apiKey + ": " + apiUrl);
  if (apiUrl.includes("/null/")) {
    console.log(`BAD API URL for ${apiKey}`);
    return;
  }
  let apiToken = "";
  if (!apiDict[apiKey].public) {
    apiToken =
      apiDict[apiKey].primary && reduxState.primaryAuthToken
        ? reduxState.primaryAuthToken
        : reduxState.authToken;
  }
  const headers =
    apiToken && !apiDict[apiKey].public
      ? authHeaders(apiToken)
      : UpduoServiceObject.simpleHeaders;
  let responseStatus: number;
  return fetch(apiUrl, {
    method: apiDict[apiKey].method,
    headers,
    body: JSON.stringify(body),
  })
    .then((response) => {
      responseStatus = response.status;
      if (response.ok) {
        return response.text();
      } else if (responseStatus === 499 || responseStatus === 503) {
        // When API request times out, simply try again (TODO: smarter handling of this)
        callApi(
          apiKey,
          body,
          successCallback,
          failureCallback,
          extraValue,
          queryString
        );
        throw Error("timeout_retry");
      } else if (responseStatus === 401) {
        throw Error("unauthorized");
      }
      throw response.json();
    })
    .then((responseText) =>
      responseText.length ? JSON.parse(responseText) : {}
    )
    .then((responseJson) => {
      // console.log(apiKey + " responseJson:");
      // console.log(responseStatus);
      // console.log(responseJson);
      if (successCallback) {
        successCallback(responseJson, responseStatus);
      }
    })
    .catch((errorJson) => {
      console.log(`API failed for: ${apiKey}`);
      console.log(responseStatus);
      console.log(errorJson);
      if (errorJson.message === "timeout_retry") {
        // Do nothing; the retry is already being handled
        return;
      } else if (errorJson.message === "unauthorized") {
        logoutResetState();
      } else if (failureCallback) {
        failureCallback(errorJson, responseStatus);
      }
    });
}

export async function asyncCallApi(
  apiKey: string,
  body?: any,
  extraValue?: number,
  queryObject?: object
) {
  const reduxState = store.getState() as any;
  if (!UpduoServiceObject.simpleHeaders) {
    // If basic header is not defined yet, set it
    await asyncDefineApiHeaders();
  }
  const controller = new AbortController();
  // 7 sec timeout
  const timeoutId = setTimeout(() => controller.abort(), 7000);
  let apiUrl = process.env.REACT_APP_SERVER_URL! + apiDict[apiKey].urlTail;
  if (extraValue || extraValue === 0) {
    apiUrl += `${extraValue}/`;
  }
  if (queryObject) {
    apiUrl += `?${qs.stringify(queryObject)}`;
  }
  const apiToken = apiDict[apiKey].public
    ? ""
    : apiDict[apiKey].primary && reduxState.primaryAuthToken
    ? reduxState.primaryAuthToken
    : reduxState.authToken;
  const headers = apiToken
    ? authHeaders(apiToken)
    : UpduoServiceObject.simpleHeaders;
  try {
    const response = await fetch(apiUrl, {
      method: apiDict[apiKey].method,
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    // clears abort timing fn if request completes in time
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    // To catch timeouts, use error.name === 'AbortError'
    console.log(`API failed for: ${apiKey}`);
    console.error(error);
  }
}
